import * as React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import Link from '@mui/material/Link';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';

import getMPTheme from './theme/getMPTheme';
import { CONTACT_EMAIL, CONTRIBUTOR_FUNNEL_SLUG, VERTICALS } from './constants';
import { trackLinkedInConversion } from '../linkedin-ad-conversion-tracking/linkedin';
import { LINKEDIN_CONV_ID_CONTRIBUTOR_PAGE_VIEWED } from '../linkedin-ad-conversion-tracking/conversionIds';
import { trackFunnel } from '../funnel/tracking';

const whatYouDo = [
  {
    title: 'Screen-share your real workflow',
    description:
      'Not a survey, not a focus group. You open the tool you already use and walk through a task while an AI moderator asks follow-up questions.',
  },
  {
    title: 'About 45 minutes, whenever suits you',
    description: 'No scheduling with a human, no calendar tag.',
  },
  {
    title: 'No preparation',
    description:
      'We want your normal workflow with its normal workarounds — that’s the whole point.',
  },
];

// Must stay identical to the privacy copy on /participate/<slug>. There is no
// anonymization pipeline — describe the human review gate and the pre-flight
// instruction, both of which are real. See MARKETING_REFRESH_PLAN.md §3.
const privacyPoints = [
  'Every recording is reviewed by a person before it is shared with anyone.',
  'You control what’s on screen — we’ll ask you to close unrelated tabs and avoid showing customer names or account numbers, and we’ll remind you before recording starts.',
  'You have 48 hours after processing to request edits or full removal, no questions asked.',
];

const faqs = [
  {
    question: 'Who sees my recording?',
    answer:
      'Product teams who subscribe to research on the tool you’re using. They see how the product is used and where it gets in the way. Your name and your company’s name are removed before anything is shared.',
  },
  {
    question: 'Do I need permission from my employer?',
    answer:
      'Use your judgment about your company’s policies. We ask you to avoid showing customer data or anything confidential, and you can decline any question or stop at any point.',
  },
  {
    question: 'What if I change my mind afterwards?',
    answer:
      'Email us within 48 hours of the session and we’ll delete it. You keep the payment.',
  },
  {
    question: 'How do I get paid?',
    answer:
      'Pick PayPal, Venmo, or an Amazon or Visa gift card at the end of the session. Payment goes out within five business days.',
  },
];

export default function ContributorPage() {
  const [mode, setMode] = React.useState('light');
  const [expanded, setExpanded] = React.useState(false);
  const MPTheme = createTheme(getMPTheme(mode));
  const navigate = useNavigate();

  React.useEffect(() => {
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode) {
      setMode(savedMode);
    } else {
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setMode(systemPrefersDark ? 'dark' : 'light');
    }
  }, []);

  React.useEffect(() => {
    // This is the top of the funnel and usually the ad's landing URL, so it is
    // where first-touch attribution gets captured. trackFunnel snapshots the
    // UTM parameters on this call; by /participate they are gone from the URL.
    trackFunnel('contributor_page_viewed', {}, { slug: CONTRIBUTOR_FUNNEL_SLUG || null });
    trackLinkedInConversion(LINKEDIN_CONV_ID_CONTRIBUTOR_PAGE_VIEWED);
  }, []);

  // Single-page app: index.html carries the buyer-page title, so set our own.
  React.useEffect(() => {
    const previous = document.title;
    document.title = 'Get paid for your product know-how — Conversation Intelligence';
    return () => {
      document.title = previous;
    };
  }, []);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const startFunnel = () => {
    if (CONTRIBUTOR_FUNNEL_SLUG) {
      navigate(`/participate/${CONTRIBUTOR_FUNNEL_SLUG}`);
      return;
    }
    // No open study yet — collect interest rather than link into a dead funnel.
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
      'I’d like to be a paid contributor',
    )}`;
  };

  const ctaLabel = CONTRIBUTOR_FUNNEL_SLUG ? 'See if you qualify' : 'Tell us you’re interested';

  return (
    <ThemeProvider theme={MPTheme}>
      <CssBaseline enableColorScheme />

      <AppBar
        position="fixed"
        sx={{ backgroundColor: 'black', boxShadow: 1, top: 0 }}
      >
        <Container maxWidth="lg">
          <Toolbar variant="dense" disableGutters sx={{ gap: 1 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <img
                src="/CIAlogo.png"
                alt="Conversation Intelligence"
                style={{ height: 40, width: 'auto', paddingRight: '10px' }}
              />
              Conversation Intelligence
            </Box>
            <Button color="primary" variant="contained" size="small" onClick={startFunnel}>
              {ctaLabel}
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero */}
      <Box
        sx={(theme) => ({
          width: '100%',
          backgroundRepeat: 'no-repeat',
          backgroundImage:
            'radial-gradient(ellipse 80% 50% at 50% -20%, hsl(0, 0%, 75%), transparent)',
          ...theme.applyStyles('dark', {
            backgroundImage:
              'radial-gradient(ellipse 80% 50% at 50% -20%, hsl(0, 0%, 25%), transparent)',
          }),
        })}
      >
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: { xs: 14, sm: 20 },
            pb: { xs: 8, sm: 12 },
            gap: 2,
          }}
        >
          <Typography
            variant="h1"
            sx={{ textAlign: 'center', fontSize: 'clamp(2.5rem, 9vw, 3.5rem)' }}
          >
            Get paid for your product know-how.
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              fontWeight: 400,
              width: { sm: '100%', md: '70%' },
            }}
          >
            Spend 45 minutes showing us how you actually use the software you work in every day.
            We pay $100–$600 depending on the depth of expertise involved.
          </Typography>
          <Button variant="contained" color="primary" size="medium" onClick={startFunnel}>
            {ctaLabel}
          </Button>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Paid by PayPal, Venmo, or an Amazon or Visa gift card, within 5 business days.
          </Typography>
        </Container>
      </Box>

      {/* What you'll actually do */}
      <Container sx={{ pt: { xs: 4, sm: 10 }, pb: { xs: 6, sm: 10 } }}>
        <Typography component="h2" variant="h4" gutterBottom sx={{ color: 'text.primary' }}>
          What you’ll actually do
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {whatYouDo.map((item) => (
            <Grid size={{ xs: 12, md: 4 }} key={item.title}>
              <Card variant="outlined" sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {item.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Divider />

      {/* What it pays */}
      <Container sx={{ py: { xs: 6, sm: 10 } }}>
        <Box sx={{ width: { sm: '100%', md: '70%' } }}>
          <Typography component="h2" variant="h4" gutterBottom sx={{ color: 'text.primary' }}>
            What it pays
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Payment scales with the expertise involved — a bookkeeper-level walkthrough pays around
            $100; a session that needs CFO-level judgment can pay up to $600. The exact amount is
            shown before you start, and it never changes after.
          </Typography>
        </Box>
      </Container>

      <Divider />

      {/* Privacy & safety */}
      <Container sx={{ py: { xs: 6, sm: 10 } }}>
        <Box sx={{ width: { sm: '100%', md: '70%' } }}>
          <Typography component="h2" variant="h4" gutterBottom sx={{ color: 'text.primary' }}>
            Your privacy &amp; safety
          </Typography>
          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            {privacyPoints.map((point) => (
              <Typography
                component="li"
                variant="body1"
                key={point}
                sx={{ color: 'text.secondary', mb: 1 }}
              >
                {point}
              </Typography>
            ))}
          </Box>
        </Box>
      </Container>

      <Divider />

      {/* Who we're looking for */}
      <Container sx={{ py: { xs: 6, sm: 10 } }}>
        <Box sx={{ width: { sm: '100%', md: '70%' } }}>
          <Typography component="h2" variant="h4" gutterBottom sx={{ color: 'text.primary' }}>
            Who we’re looking for right now
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
            People who use these tools as part of their day job — not evaluators, not
            administrators who set them up once. If you use one of them weekly, you qualify.
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3 }}>
            {VERTICALS.map((vertical) => (
              <Typography
                component="li"
                variant="body1"
                key={vertical.name}
                sx={{ color: 'text.secondary', mb: 0.5 }}
              >
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {vertical.name}
                </Box>{' '}
                — {vertical.products}
              </Typography>
            ))}
          </Box>
          <Button variant="contained" color="primary" onClick={startFunnel}>
            {ctaLabel}
          </Button>
        </Box>
      </Container>

      <Divider />

      {/* FAQ */}
      <Container sx={{ py: { xs: 6, sm: 10 } }}>
        <Typography component="h2" variant="h4" gutterBottom sx={{ color: 'text.primary' }}>
          Questions
        </Typography>
        <Box sx={{ width: '100%', mt: 2 }}>
          {faqs.map((faq, index) => {
            const panel = `contrib${index + 1}`;
            return (
              <Accordion
                key={panel}
                expanded={expanded === panel}
                onChange={handleChange(panel)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${panel}d-content`}
                  id={`${panel}d-header`}
                >
                  <Typography component="h3" variant="subtitle2">
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ maxWidth: { sm: '100%', md: '70%' } }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Container>

      <Container
        component="footer"
        sx={{
          py: { xs: 6, sm: 8 },
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          © {new Date().getFullYear()} Conversation Intelligence
        </Typography>
        <Link color="text.secondary" variant="body2" href={`mailto:${CONTACT_EMAIL}`}>
          {CONTACT_EMAIL}
        </Link>
      </Container>
    </ThemeProvider>
  );
}
